import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { cldSquare } from "../lib/img";

/**
 * Product search — two presentations:
 *  - variant="overlay": full-screen modal opened from the navbar (Amazon-style).
 *  - variant="mobile":  inline block inside the hamburger drawer.
 *
 * NOTE: JSX for the input + result list is inlined (not extracted into inner
 * components) so React reuses the same DOM nodes on re-render — otherwise the
 * <input> unmounts/remounts on every keystroke and loses focus after 1 char.
 */
export function SearchBar({ variant = "overlay", onClose, onNavigate }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const isOverlay = variant === "overlay";

  // Auto-focus when overlay opens
  useEffect(() => {
    if (isOverlay) {
      const t = setTimeout(() => inputRef.current?.focus(), 40);
      return () => clearTimeout(t);
    }
  }, [isOverlay]);

  // Esc to close overlay
  useEffect(() => {
    if (!isOverlay) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOverlay, onClose]);

  // Lock body scroll while overlay is open
  useEffect(() => {
    if (!isOverlay) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [isOverlay]);

  // Debounced fetch
  useEffect(() => {
    if (!q.trim()) { setResults([]); setTotal(0); setBusy(false); return; }
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q, limit: 8 } });
        setResults(Array.isArray(data.items) ? data.items : []);
        setTotal(data.total || 0);
      } catch {
        setResults([]); setTotal(0);
      }
      setBusy(false);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const submit = (e) => {
    e?.preventDefault?.();
    const query = q.trim();
    if (!query) return;
    onClose?.();
    onNavigate?.();
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const handleResultClick = () => {
    onClose?.();
    onNavigate?.();
    setQ("");
  };

  const inputEl = (
    <form onSubmit={submit} className="relative">
      <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-black" />
      <input
        ref={inputRef}
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search chains, watches, grillz…"
        aria-label="Search products"
        data-testid={`search-input-${variant}`}
        className={
          isOverlay
            ? "w-full border-2 border-black bg-white py-4 pl-12 pr-12 text-base text-black placeholder-zinc-400 focus:outline-none focus:ring-4 focus:ring-black/10 sm:text-lg"
            : "w-full border-2 border-black bg-white py-3 pl-11 pr-11 text-sm text-black placeholder-zinc-400 focus:outline-none"
        }
      />
      {q && (
        <button
          type="button"
          onClick={() => { setQ(""); inputRef.current?.focus(); }}
          aria-label="Clear search"
          data-testid={`search-clear-${variant}`}
          className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center text-zinc-500 hover:text-black"
        >
          <X size={16} />
        </button>
      )}
    </form>
  );

  const rowsEl = (
    <ul className="divide-y-2 divide-black/10">
      {results.map((p) => (
        <li key={p.slug}>
          <Link
            to={`/${p.category_slug}/${p.slug}`}
            onClick={handleResultClick}
            data-testid={`search-result-${p.slug}`}
            className="flex items-center gap-4 px-4 py-3 transition-colors duration-150 hover:bg-zinc-50 sm:px-6"
          >
            <div className="h-16 w-16 shrink-0 overflow-hidden border-2 border-black bg-zinc-100">
              {p.images?.[0] && (
                <img src={cldSquare(p.images[0], 160)} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-black sm:text-base">{p.name}</p>
              <p className="mt-1 truncate text-[10px] uppercase tracking-widest text-zinc-500">{p.category_slug}</p>
            </div>
            <div className="shrink-0 text-right tabular-nums">
              <p className="text-sm font-black text-black sm:text-base">${p.price.toFixed(2)}</p>
              {p.compare_at_price ? (
                <p className="text-xs text-zinc-400 line-through">${p.compare_at_price.toFixed(2)}</p>
              ) : null}
            </div>
          </Link>
        </li>
      ))}
      {total > results.length && (
        <li>
          <button
            type="button"
            onClick={submit}
            data-testid={`search-view-all-${variant}`}
            className="block w-full bg-black px-4 py-4 text-left text-xs font-bold uppercase tracking-[0.3em] text-white hover:bg-zinc-800 sm:px-6"
          >
            View all {total} results for &quot;{q}&quot; →
          </button>
        </li>
      )}
    </ul>
  );

  // -------- Mobile (inline in drawer) --------
  if (!isOverlay) {
    const hasQuery = q.trim().length > 0;
    return (
      <div className="w-full" data-testid={`search-${variant}`}>
        {inputEl}
        {hasQuery && (
          <div className="mt-3 border-2 border-black bg-white" data-testid={`search-dropdown-${variant}`}>
            {busy && (
              <div className="flex items-center gap-2 px-4 py-4 text-xs text-zinc-500">
                <Loader2 size={13} className="animate-spin" /> Searching…
              </div>
            )}
            {!busy && results.length === 0 && (
              <p className="px-4 py-4 text-xs text-zinc-500" data-testid={`search-empty-${variant}`}>
                No matches. Press Enter to open the search page.
              </p>
            )}
            {!busy && results.length > 0 && rowsEl}
          </div>
        )}
      </div>
    );
  }

  // -------- Overlay --------
  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center"
      data-testid="search-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Search products"
    >
      {/* backdrop */}
      <button
        type="button"
        aria-label="Close search"
        onClick={onClose}
        data-testid="search-overlay-backdrop"
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      />
      {/* panel */}
      <div className="relative mt-16 w-[92%] max-w-3xl sm:mt-24">
        <div className="border-2 border-black bg-white shadow-[10px_10px_0_#000]">
          <div className="flex items-center justify-between border-b-2 border-black px-4 py-3 sm:px-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-zinc-500">Search VVS Lab</p>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="search-overlay-close"
              className="flex h-8 w-8 items-center justify-center border-2 border-black bg-white text-black hover:bg-black hover:text-white"
            >
              <X size={14} />
            </button>
          </div>
          <div className="p-4 sm:p-6">
            {inputEl}
            <div className="mt-4 max-h-[60vh] overflow-y-auto">
              {!q.trim() && (
                <p className="px-2 py-6 text-center text-xs text-zinc-500">
                  Start typing — chain widths, watch styles, SKU codes… all searchable.
                </p>
              )}
              {q.trim() && busy && (
                <div className="flex items-center gap-2 px-2 py-4 text-xs text-zinc-500">
                  <Loader2 size={13} className="animate-spin" /> Searching…
                </div>
              )}
              {q.trim() && !busy && results.length === 0 && (
                <p className="px-2 py-4 text-xs text-zinc-500" data-testid={`search-empty-${variant}`}>
                  No matches for &quot;{q}&quot;. Press Enter to open the full results page.
                </p>
              )}
              {!busy && results.length > 0 && rowsEl}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
